/**
 * @file src/input/SensorsController.js
 * @description Abstraction over internal (DeviceMotion) and external sensor streams,
 * fanning out motion data to rack axes and managing UI toggle state per dimension.
 */

import { MathUtils, Quaternion, Euler } from 'three';
import {
    INTERNAL_SENSORS_USABLE,
    EXTERNAL_SENSORS_USABLE,
    isMobileDevice,
    SENSOR_FEEDBACK_THROTTLE_MS,
    getPriority,
    DEFAULT_PRIORITY,
} from '../config/Constants.js';
import {
    anySensorDriven,
    attachSensorDevice,
    defaultSensorVoiceId,
    drivenDimensions,
    enabledDimensions,
    externalTargetIds,
    isSensorEnabledOn,
    pressSensor,
    setExternalTargets,
    syncDeviceToSwitch,
} from './sensorSwitches.js';
import { createInputRouter, declareGeneratorSource } from './source/InputRouter.ts';
import { resolveScopedContext } from './source/ScopingResolver.ts';
import { voiceRegistry } from '../voice/VoiceRegistry.js';
import { broadcastAction } from '../multi/multiFocusBroadcast.js';
import { syncCoordinator } from '../sync/SyncCoordinator.js';
import { createDebugLog } from '../utils/debugLog.js';

/**
 * Sensor progress reporting: which sensors were found, what was bound, when calibration ran. All of
 * it describes a healthy run, so none of it belongs in every session's console — `window.__DEBUG_SENSORS
 * = true` brings it back. What stays unconditional is anything that means a sensor stream is actually
 * broken: denied permission, malformed readings, a failed write.
 */
const debug = createDebugLog('__DEBUG_SENSORS', '[SensorController]');

/**
 * The ONE identity this controller writes ParameterManager under — the id its input-router handle
 * is minted with (declared a generator there) and the `sourceController` the multi-focus gang-write
 * broadcasts, so both paths classify identically everywhere the id is read.
 */
export const SENSOR_SOURCE_ID = 'SensorController';

// Tilt is a machine stream of absolute samples — the next reading supersedes any rollback — and that
// is true of the id itself, not of the router the local path happens to mint. Declared here so the
// phone path (which writes each target voice's own engine surface, never this controller's router)
// is recognised as a generator from its first sample; without it a room republishes every tilt
// sample as an un-thinned hand commit.
declareGeneratorSource(SENSOR_SOURCE_ID);

// Per-axis arbitration priority resolved once from the single source of truth
// (PRIORITY_MAP), not per sensor sample — commitAxisValue runs on the hot device-motion path.
const SENSOR_AXIS_PRIORITY = {
    x: getPriority('sensor-x'),
    y: getPriority('sensor-y'),
    z: getPriority('sensor-z'),
    distance: getPriority('sensor-distance'),
};

/**
 * @class SensorController
 * @description Manages device orientation and motion sensor inputs and maps them to user parameters.
 * Handles toggle-based activation/deactivation of sensor axes (x, y, z, distance).
 * Integrates with a user-defined `ParameterManager` for real-time updates.
 */
export class SensorController {
    // Private static instance variable
    static #instance = null;

    /**
     * Returns the existing instance without creating a new one.
     * @returns {SensorController|null} The current instance if initialized, otherwise null.
     */
    static getExistingInstance() {
        return SensorController.#instance;
    }

    /**
     * Indicates whether the controller has already been instantiated.
     * @returns {boolean} True if an instance exists, false otherwise.
     */
    static hasInstance() {
        return SensorController.#instance !== null;
    }

    /**
     * Returns the singleton instance of SensorController.
     * @param {ParameterManager} parameterManager - The user manager instance.
     * @returns {SensorController} The singleton instance.
     */
    static getInstance(parameterManager) {
        if (!SensorController.#instance) {
            SensorController.#instance = new SensorController(parameterManager);
        }
        return SensorController.#instance;
    }

    /**
     * Private constructor to prevent direct instantiation.
     * @param {ParameterManager} parameterManager - The user manager instance.
     */
    constructor(parameterManager) {
        if (SensorController.#instance) {
            throw new Error('Use SensorController.getInstance() to get the singleton instance.');
        }

        // Initialize properties
        this.isSensorActive = false;
        this.parameterManager = parameterManager;
        // The seam this controller pushes through. Lazily bound (see _inputSource())
        // because parameterManager may not be ready at construction.
        this._inputSourceHandle = null;
        // The DEVICE flags: which axes this device is feeding on the focused deck's current dimension.
        // The SWITCHES live in `sensorSwitches`, one per deck per dimension; this only ever follows them.
        this.activeAxes = { x: false, y: false, z: false, distance: false };
        this.toggleElements = {};
        this.axisThrottleState = new Map();
        this.isMobileRuntime = typeof navigator !== 'undefined' && isMobileDevice();
        this.throttleUpdate = this.throttle(this.updateParameters.bind(this), SENSOR_FEEDBACK_THROTTLE_MS); // 16-50ms depending on preset
        this._boundDimensionListener = null;

        // Current normalized values
        this.currentYaw = 0.5;   // Normalized [0,1], 0.5 is center
        this.currentPitch = 0.5; // Normalized [0,1], 0.5 is center
        this.currentRoll = 0.5;  // Normalized [0,1], 0.5 is center

        // Quaternion tracking
        this.currentQuaternion = new Quaternion(); // Represents the current orientation
        this.calibrationQuatInverse = null; // Inverse of calibration quaternion

        // Pre-allocated objects for sensor event processing (avoid per-event allocation)
        this._tempEuler = new Euler();
        this._tempQuaternion = new Quaternion();
        // Dead zone threshold — changes smaller than this are ignored to reduce jitter
        this.DEAD_ZONE = 0.005;

        // Debug log timing
        this.lastDebugTime = 0;

        // Motion tracking
        this.velocityY = 0;
        this.positionY = 0;
        this.initialAccelerationY = 0;
        this.lastTimestamp = null;
        this.calibrated = false;

        // Bind handleDeviceOrientation and handleDeviceMotion once to maintain reference
        this.boundHandleDeviceOrientation = this.handleDeviceOrientation.bind(this);
        this.boundHandleDeviceMotion = this.handleDeviceMotion.bind(this);

        // Initialize toggles and calibration button
        this.initializeToggles();
        this.initializeCalibrationButton();

        // This is the one device the switches can drive. On every dimension change the device
        // re-reads the focused deck's switches for the dimension it is now on and starts or stops
        // accordingly — that is what keeps "which axes am I feeding" describing the dimension you are on.
        attachSensorDevice(this);
        syncDeviceToSwitch(defaultSensorVoiceId());
        this._boundDimensionListener = () => syncDeviceToSwitch(defaultSensorVoiceId());
        if (typeof document !== 'undefined') {
            document.addEventListener('orbiters:dimension-changed', this._boundDimensionListener);
        }

        // The sensor connection is ONE shared device stream, but a shared realm has N orbiters.
        // Route its data to whichever voice is FOCUSED — bind this controller's target ParameterManager
        // to the active voice and re-point it (rebuilding the input router) whenever focus changes. So one
        // phone drives the orbiter you're working on; switch focus and the data follows. This aligns the
        // WRITE target with the dimension SCOPE, which already follows the active voice (getScopedContext →
        // resolveScopedContext). Single-orbiter has one voice → resolves to it and never changes.
        this._rebindToActiveVoice();
        this._unsubscribeActiveVoice =
            typeof voiceRegistry.onActiveChange === 'function'
                ? voiceRegistry.onActiveChange(() => {
                    // Route the shared phone stream to the newly-focused voice, and follow ITS own
                    // switches: each deck keeps its own dimensions × axes enables in `sensorSwitches`;
                    // only the one live device connection is shared, and it goes where the focus goes.
                    this._rebindToActiveVoice();
                    syncDeviceToSwitch(defaultSensorVoiceId());
                })
                : null;

        // Determine whether internal or external sensors are usable
        this.useInternalSensors = INTERNAL_SENSORS_USABLE;
        this.useExternalSensors = EXTERNAL_SENSORS_USABLE;

        // Initialize sensors based on availability
        if (this.useInternalSensors) {
            this.initializeInternalSensors();
        } else if (this.useExternalSensors) {
            this.initializeExternalSensors();
        } else {
            // A desktop with no paired phone has no usable sensors — that is the normal case here.
            debug('no usable sensors detected');
        }

        // Initiate calibration
        this.calibrateDevice();
    }

    /**
     * Initializes the toggles for sensor axes (x, y, z, distance) and binds their change events.
     * Assumes toggles are custom web components with a 'state' attribute.
     * Logs the initial state of each toggle for debugging purposes.
     */
    initializeToggles() {
        ['toggleSensorX', 'toggleSensorY', 'toggleSensorZ', 'toggleSensorDistance'].forEach((id) => {
            const toggle = document.getElementById(id);
            const axis = id.replace('toggleSensor', '').toLowerCase();

            if (toggle) {
                debug('bound toggle', { id, axis, state: toggle.state });

                this.toggleElements[axis] = toggle;
                const componentId = `${axis}.sensor-toggle`;
                toggle.dataset.midiComponentId = componentId;
                toggle.setAttribute('data-midi-param-id', componentId);
                if (typeof window !== 'undefined' && typeof window.registerAutomatableElement === 'function') {
                    window.registerAutomatableElement(toggle, { midiParamId: componentId, automatable: true });
                }

                // A legacy element press is a press on the focused deck's switch, like any other.
                toggle.addEventListener('change', () => {
                    const on = this.normalizeToggleState(toggle.state ?? toggle.value ?? toggle.checked);
                    if (axis === 'distance') this.setAxisActive('distance', on);
                    else pressSensor(defaultSensorVoiceId(), axis, on);
                });
            } else {
                // These are the legacy `<webaudio-switch>` toggles the React sensors surface replaced.
                // They are absent on every healthy run, so their absence is not news.
                debug('legacy toggle element absent', { id });
            }
        });
    }

    /**
     * Checks if the device supports orientation sensors.
     * @static
     * @returns {boolean} True if `DeviceOrientationEvent` is supported, false otherwise.
     */
    static isSupported() {
        return typeof DeviceOrientationEvent !== 'undefined';
    }

    /**
     * Initializes internal sensors.
     * Placeholder for any internal sensor initialization logic.
     */
    initializeInternalSensors() {
        debug('initializing internal sensors');
        // Add internal sensor initialization logic here if needed.
    }

    /**
     * Initializes external sensors.
     * Placeholder for any external sensor initialization logic.
     */
    initializeExternalSensors() {
        debug('initializing external sensors');
        // Add external sensor initialization logic here if needed.
    }

    /**
     * Requests user permission to access orientation sensors (iOS 13+ only).
     * @async
     * @returns {Promise<boolean>} Resolves to `true` if permission is granted, `false` otherwise.
     */
    async requestPermission() {
        if (
            typeof DeviceOrientationEvent !== 'undefined' &&
            typeof DeviceOrientationEvent.requestPermission === 'function'
        ) {
            try {
                const response = await DeviceOrientationEvent.requestPermission();
                return response === 'granted';
            } catch (error) {
                console.error('SensorController: Error requesting permission:', error);
                return false;
            }
        }
        return true; // Assume permission is granted on non-iOS devices.
    }

    /**
     * Activates the device orientation and motion sensors if supported and permission is granted.
     * Binds the `deviceorientation` and `devicemotion` events to listen for changes.
     * @async
     */
    async activateSensors() {
        if (!SensorController.isSupported()) {
            // Normal on any desktop browser; the UI is what tells the user, not the console.
            debug('device orientation not supported here');
            return;
        }

        const permissionGranted = await this.requestPermission();
        if (!permissionGranted) {
            console.warn('SensorController: Permission to access sensors denied.');
            return;
        }

        this.startListening();
        this.isSensorActive = true;
        debug('sensors activated');
    }

    /**
     * Starts listening for `deviceorientation` and `devicemotion` events to capture orientation and motion changes.
     * Ensures that only one listener is active at a time.
     * @private
     */
    startListening() {
        if (!this.isSensorActive) {
            window.addEventListener('deviceorientation', this.boundHandleDeviceOrientation, true);
            window.addEventListener('devicemotion', this.boundHandleDeviceMotion, true);
            debug('listening to deviceorientation + devicemotion');
        }
    }

    /**
     * Stops listening for `deviceorientation` and `devicemotion` events.
     * Resets the `isSensorActive` flag.
     * @public
     */
    stopListening() {
        if (this.isSensorActive) {
            window.removeEventListener('deviceorientation', this.boundHandleDeviceOrientation, true);
            window.removeEventListener('devicemotion', this.boundHandleDeviceMotion, true);
            this.isSensorActive = false;
            this.clearMobileThrottleState();
            debug('stopped listening to deviceorientation + devicemotion');
        }
    }

    /**
     * Calibrates the device by setting initial reference points for orientation.
     * Prompts the user to hold the device steady near their body.
     * @private
     */
    calibrateDevice() {
        debug('starting calibration');
      
        // If we are using external sensors on a desktop, just grab the last known data
        // If we don't have it yet, we show a warning and exit.
        if (this.useExternalSensors) {
          this.calibrateFromExternal();
        } else {
          this.calibrateFromInternal();
        }
      }

      calibrateFromInternal() {
        const onCalibrate = (event) => {
          if (event.alpha != null && event.beta != null && event.gamma != null) {
            this.finishCalibration(event.alpha, event.beta, event.gamma);
            window.removeEventListener('deviceorientation', onCalibrate);
          }
        };
      
        // Listen for one valid reading
        window.addEventListener('deviceorientation', onCalibrate, { once: true });
      }
      
      calibrateFromExternal() {
        // Use the last external data your app received from the remote sensor
        if (!this.lastExternalSensorData) {
          // Calibration runs at init, so with no phone paired this is the normal desktop path — a
          // STATE, not a fault, hence gated. Deliberately louder than its sibling below: data that
          // arrived but is missing alpha/beta/gamma means a broken stream, and that still warns.
          // (Gap worth closing separately: pressing Calibrate with a paired-but-silent phone now
          // reports nothing to the USER either — that wants a toast, not a console line.)
          debug('no external sensor data yet — nothing to calibrate against');
          return;
        }
        const { alpha, beta, gamma } = this.lastExternalSensorData;
        if (alpha == null || beta == null || gamma == null) {
          console.warn('External sensor data is missing alpha/beta/gamma. Cannot calibrate.');
          return;
        }
        this.finishCalibration(alpha, beta, gamma);
      }
      
      // Central place to store calibration info — stores as inverse quaternion
      finishCalibration(alpha, beta, gamma) {
        this.initialAlpha = alpha;
        this.initialBeta = beta;
        this.initialGamma = gamma;

        // Build calibration quaternion from the reference pose
        this._tempEuler.set(
            MathUtils.degToRad(beta),
            MathUtils.degToRad(gamma),
            MathUtils.degToRad(alpha),
            'YXZ'
        );
        this.calibrationQuatInverse = new Quaternion()
            .setFromEuler(this._tempEuler)
            .invert();

        this.calibrated = true;
      }


    /**
     * Handles `deviceorientation` events, maps yaw, pitch, and roll directly to normalized X, Y, Z.
     * Prevents overshooting and sticks to limits until direction changes.
     * Skips processing for inactive axes for efficiency.
     * @param {DeviceOrientationEvent} event - Orientation event containing `alpha`, `beta`, and `gamma`.
     */
    handleDeviceOrientation(event) {
       // console.log('[SensorController] Received deviceorientation event:', event);
        this.processSensorData(event, false);
    }

  getCurrentDimensionId() {
    const { dimensionId } = this.getScopedContext();
    return dimensionId ?? null;
  }

  /**
   * Every dimension of the focused deck, with its x/y/z switches — what the paired phone is shown.
   * Keyed by dimension id; `default` stands for a deck with no active dimension.
   */
  /**
   * Every dimension of ONE deck, with its x/y/z switches — what the paired phone is shown.
   * Keyed by dimension id; `default` stands for a deck with no active dimension. The deck is the
   * phone's primary target when it has one, else the focused deck; `voiceId: null` means the phone
   * drives nothing, and every lamp it is shown reads off — it cannot press any of them.
   */
  getAllDimensionToggleStates({ voiceId = defaultSensorVoiceId() } = {}) {
    const scopeVoice = voiceRegistry.get(voiceId ?? defaultSensorVoiceId()) ?? voiceRegistry.getActive();
    const dimensionIds = new Set();
    const { dimensionId: currentDimension } = resolveScopedContext({ modeController: scopeVoice?.worldMode ?? null });
    if (currentDimension) {
      dimensionIds.add(currentDimension);
    }
    for (const axis of ['x', 'y', 'z']) {
      for (const dimensionId of enabledDimensions(voiceId, axis)) dimensionIds.add(dimensionId);
    }
    const wm = scopeVoice?.worldMode;
    const definitions = wm?.getAvailableDimensions?.() ?? wm?.dimensionList;
    if (Array.isArray(definitions)) {
      definitions.forEach((entry) => dimensionIds.add(entry?.id ?? entry));
    }

    const snapshot = {};
    dimensionIds.forEach((dimensionId) => {
      snapshot[dimensionId ?? 'default'] = this.getToggleStates(dimensionId, voiceId);
    });
    return snapshot;
  }

  /**
   * The orbiters a PAIRED PHONE drives: the phone's set and nobody else's. The screen's focus and
   * multi-focus never move it, and it never moves them. Empty means the phone drives NOTHING, which is
   * where a fresh pairing starts — the player picks decks on the phone before their tilt or their
   * toggles reach anything. Held by the switch store, which is what answers "can this client reach
   * that deck"; only the external (phone) path reads it, and this client's OWN device still follows
   * the focused voice, as it always has.
   */
  setExternalTargetVoices(ids) {
    setExternalTargets(ids);
  }

  /** Is there any reason for the device to keep running: anything this device drives on the focused deck? */
  hasAnySensorAxisActive() {
    return this.activeAxes.distance === true || anySensorDriven(defaultSensorVoiceId());
  }

  /**
   * The dimensions an axis is allowed to drive: exactly those of the focused deck whose OWN switch
   * this device is driving — not the one you happen to be on. Switch away from the dimension where
   * you enabled an axis and the sensor must not follow you into one whose switch plainly reads off;
   * and a switch a room peer claimed is theirs to feed, however lit it is here.
   */
  getActiveDimensionsForAxis(axis) {
    // `distance` is the exception, and deliberately so: it has no per-dimension switch in the UI, so
    // the device flag is its ONLY state, and it follows the current dimension.
    if (axis === 'distance') {
      const currentDimension = this.getCurrentDimensionId();
      return this.activeAxes.distance && currentDimension ? [currentDimension] : [];
    }
    return drivenDimensions(defaultSensorVoiceId(), axis);
  }
  
  /**
   * Processes sensor data and maps it to normalized x, y, z values using quaternions.
   * Ensures smooth, continuous mapping without gimbal lock or jumps.
   * @param {Object} event - Sensor data ({ alpha, beta, gamma } in degrees).
   * @param {boolean} isExternal - Whether the data is from an external source.
   */
  processSensorData(event, isExternal = false) {
      // 1) Build quaternion from raw Euler angles
      const rawAlpha = event.alpha ?? 0;
      const rawBeta  = event.beta  ?? 0;
      const rawGamma = event.gamma ?? 0;

      this._tempEuler.set(
          MathUtils.degToRad(rawBeta),   // X-axis (Pitch)
          MathUtils.degToRad(rawGamma),  // Y-axis (Roll)
          MathUtils.degToRad(rawAlpha),  // Z-axis (Yaw)
          'YXZ'
      );
      this._tempQuaternion.setFromEuler(this._tempEuler).normalize();

      // 2) Apply calibration in quaternion space (correct for non-commutative rotations)
      if (this.calibrated && this.calibrationQuatInverse) {
          this._tempQuaternion.premultiply(this.calibrationQuatInverse).normalize();
      }

      // 3) Check continuity (dot < 0 => flip signs to avoid sudden jumps)
      if (this.currentQuaternion.dot(this._tempQuaternion) < 0) {
          this._tempQuaternion.x *= -1;
          this._tempQuaternion.y *= -1;
          this._tempQuaternion.z *= -1;
          this._tempQuaternion.w *= -1;
      }

      // 4) Slerp for smooth interpolation — less smoothing for external (WebRTC)
      //    data since it already carries network latency
      const slerpFactor = isExternal ? 0.9 : 0.75;
      this.currentQuaternion.slerp(this._tempQuaternion, slerpFactor);

      // 5) Map quaternion components to [0,1] using a tighter input range.
      //    Quaternion components naturally saturate and reverse at limits — no wrapping.
      //    ±90° of rotation corresponds to q ≈ ±0.707; mapping from [-0.7, 0.7]
      //    gives full [0,1] output range for typical phone movement, clamping beyond.
      const Q_RANGE = 0.7;
      const normalizedX = this.mapRange(this.currentQuaternion.z, -Q_RANGE, Q_RANGE, 0, 1); // Yaw
      const normalizedY = this.mapRange(this.currentQuaternion.x, -Q_RANGE, Q_RANGE, 0, 1); // Pitch
      const normalizedZ = this.mapRange(this.currentQuaternion.y, -Q_RANGE, Q_RANGE, 0, 1); // Roll

      // 6) Apply dead zone and push. A PHONE sample goes to the phone's own decks, each gated by its
      // own switch; the per-dimension routing below is this client's OWN device on the focused deck.
      if (isExternal) {
          if (Math.abs(normalizedX - this.currentYaw) >= this.DEAD_ZONE) this.currentYaw = normalizedX;
          if (Math.abs(normalizedY - this.currentPitch) >= this.DEAD_ZONE) this.currentPitch = normalizedY;
          if (Math.abs(normalizedZ - this.currentRoll) >= this.DEAD_ZONE) this.currentRoll = normalizedZ;
          this.commitExternalAxisValue('x', this.currentYaw);
          this.commitExternalAxisValue('y', this.currentPitch);
          this.commitExternalAxisValue('z', this.currentRoll);
          return;
      }
      const activeXDimensions = this.getActiveDimensionsForAxis('x');
      if (activeXDimensions.length) {
          if (Math.abs(normalizedX - this.currentYaw) >= this.DEAD_ZONE) {
              this.currentYaw = normalizedX;
          }
          for (let i = 0; i < activeXDimensions.length; i++) {
            this.pushAxisValue('x', this.currentYaw, activeXDimensions[i], { isExternal });
          }
      }

      const activeYDimensions = this.getActiveDimensionsForAxis('y');
      if (activeYDimensions.length) {
          if (Math.abs(normalizedY - this.currentPitch) >= this.DEAD_ZONE) {
              this.currentPitch = normalizedY;
          }
          for (let i = 0; i < activeYDimensions.length; i++) {
            this.pushAxisValue('y', this.currentPitch, activeYDimensions[i], { isExternal });
          }
      }

      const activeZDimensions = this.getActiveDimensionsForAxis('z');
      if (activeZDimensions.length) {
          if (Math.abs(normalizedZ - this.currentRoll) >= this.DEAD_ZONE) {
              this.currentRoll = normalizedZ;
          }
          for (let i = 0; i < activeZDimensions.length; i++) {
            this.pushAxisValue('z', this.currentRoll, activeZDimensions[i], { isExternal });
          }
      }

      // 7) Throttled debug log. The flag is checked FIRST: this runs on the device-motion path, and
      // building the object, stringifying it with indentation and printing it twice a second for the
      // whole session is real work on a phone — work nobody was reading. Off, this costs one
      // property read per event.
      if (typeof window !== 'undefined' && window.__DEBUG_SENSORS) {
          const now = performance.now();
          if (now - this.lastDebugTime > 500) {
              this.lastDebugTime = now;
              debug('orientation', JSON.stringify({
                  rawAngles: { alpha: rawAlpha, beta: rawBeta, gamma: rawGamma },
                  quaternion: {
                    x: this.currentQuaternion.x.toFixed(3),
                    y: this.currentQuaternion.y.toFixed(3),
                    z: this.currentQuaternion.z.toFixed(3),
                    w: this.currentQuaternion.w.toFixed(3)
                  },
                  finalNormalized: {
                    x: this.currentYaw.toFixed(3),
                    y: this.currentPitch.toFixed(3),
                    z: this.currentRoll.toFixed(3)
                  },
                  isExternal
              }, null, 2));
          }
      }
  }

  /**
   * Pushes a normalized sensor value into the ParameterManager so the audio path reacts.
   * Handles multidimensional parameters (x, y, z) by resolving the active dimension context.
   * @param {string} axis - Axis identifier ('x', 'y', 'z', or 'distance').
   * @param {number} normalizedValue - Normalized value in [0, 1].
   * @private
   */
  pushAxisValue(axis, normalizedValue, dimensionId = null, options = {}) {
    const { skipThrottle = false, isExternal = false } = options;

    if (!skipThrottle && this.handleMobileAxisThrottle(axis, normalizedValue, dimensionId, { isExternal })) {
      return;
    }

    this.commitAxisValue(axis, normalizedValue, dimensionId, { isExternal });
  }

  commitAxisValue(axis, normalizedValue, dimensionId = null, { isExternal = false } = {}) {
    // A phone sample goes to the PHONE's orbiters, never the focused one. It cannot share the path
    // below: `this.parameterManager` is bound to the focused voice and the gang runs over the
    // SCREEN's selection, so both would answer the wrong question for a phone pointed elsewhere.
    if (isExternal) {
      this.commitExternalAxisValue(axis, normalizedValue, dimensionId);
      return;
    }

    if (!this.parameterManager) {
      return;
    }

    try {
      const clamped = MathUtils.clamp(normalizedValue ?? 0, 0, 1);
      const param = this.parameterManager.parameters?.get?.(axis);

      if (!param) {
        // Parameter not registered yet; nothing to push.
        return;
      }

      // Priority from the single source of truth (PRIORITY_MAP), per axis
      // (sensor-x/y/z = 8/9/10, sensor-distance = 10.5), precomputed — not a flat 15.
      const inputSource = this._inputSource();
      if (!inputSource) return;
      const priority = SENSOR_AXIS_PRIORITY[axis] ?? DEFAULT_PRIORITY;

      if (param.isMultidimensional) {
        const { dimensionId: activeDimensionId } = this.getScopedContext();
        const preferredDimension = dimensionId === 'default' ? null : dimensionId;
        const targetDimension = preferredDimension || activeDimensionId || param.activeDimensionId;
        if (!targetDimension) {
          console.warn(`[SensorController] Cannot push axis '${axis}': no active dimension.`);
          return;
        }

        if (!this.shouldApplySensorToAxis(axis, targetDimension)) {
          return;
        }

        const dimensionState =
          typeof param.dimensions?.get === 'function'
            ? param.dimensions.get(targetDimension)
            : param.dimensions?.[targetDimension];

        const min = dimensionState?.min ?? param.min ?? 0;
        const max = dimensionState?.max ?? param.max ?? 1;
        const rawValue = this.parameterManager.denormalize(clamped, min, max);

        if (Number.isFinite(rawValue)) {
          inputSource.set(axis, rawValue, { kind: 'raw', dim: targetDimension, priority });
          // Multi-focus: the local write above hit the FOCUSED voice; when more than one orbiter
          // is selected (Decision 004), gang the same axis write across the rest of the selection via the
          // shared broadcast owner, so one phone drives every selected orbiter at once. No-op for a single
          // focus (multiFocusActive === false). Siblings write through their own raw engine command surface
          // (no re-entry into this controller → no feedback loop), applying to their own like-id dimension.
          // Each sibling's OWN switch is its gate: a deck whose X reads off does not move on X.
          broadcastAction(voiceRegistry.activeId, 'params', 'setDimensionValue', [
            axis, targetDimension, rawValue, SENSOR_SOURCE_ID, priority, undefined,
          ], { only: (siblingId) => isSensorEnabledOn(siblingId, targetDimension, axis) });
        }
      } else {
        inputSource.set(axis, clamped, { kind: 'normalized', priority });
      }
    } catch (error) {
      console.error(`[SensorController] Failed to push axis '${axis}' value:`, error);
    }
  }

  /**
   * Push one axis sample from the paired phone into every orbiter the PHONE selected.
   *
   * Each target is resolved, gated by its OWN switch on the dimension it is playing, range-checked
   * and denormalized against its OWN parameter manager, and written through its OWN engine command
   * surface — the raw one, not the broadcast-wrapped one, so the write stays out of the multi-focus
   * gang. Nothing here reads the focused voice.
   */
  commitExternalAxisValue(axis, normalizedValue, dimensionId = null) {
    const targetIds = externalTargetIds();
    if (!targetIds.length) {
      return; // the phone has no orbiter selected — drive nothing
    }

    const clamped = MathUtils.clamp(normalizedValue ?? 0, 0, 1);
    const priority = SENSOR_AXIS_PRIORITY[axis] ?? DEFAULT_PRIORITY;
    const preferredDimension = dimensionId === 'default' ? null : dimensionId;

    for (const voiceId of targetIds) {
      const voice = voiceRegistry.get(voiceId);
      const params = voice?.engineCommands?.params;
      const parameterManager = voice?.parameterManager;
      if (!params || !parameterManager) continue;

      try {
        const param = parameterManager.parameters?.get?.(axis);
        if (!param) continue; // that orbiter has not registered this axis yet

        // Each orbiter carries its own like-id dimensions and its own active one, so the dimension is
        // resolved PER VOICE; the phone's tabs drive every target's dimension together.
        const { dimensionId: voiceDimensionId } = resolveScopedContext({ modeController: voice.worldMode ?? null });
        const targetDimension = preferredDimension || voiceDimensionId || param.activeDimensionId || null;

        // The deck's own switch is the gate — the same one its tile draws.
        if (!isSensorEnabledOn(voiceId, targetDimension, axis)) continue;

        if (!param.isMultidimensional) {
          const raw = parameterManager.denormalize(clamped, param.min ?? 0, param.max ?? 1);
          if (Number.isFinite(raw)) {
            params.setValue?.(axis, raw, SENSOR_SOURCE_ID, priority, undefined);
          }
          continue;
        }
        if (!targetDimension) continue;
        if (!this.shouldApplySensorToAxis(axis, targetDimension, voice)) continue;

        const dimensionState =
          typeof param.dimensions?.get === 'function'
            ? param.dimensions.get(targetDimension)
            : param.dimensions?.[targetDimension];

        const min = dimensionState?.min ?? param.min ?? 0;
        const max = dimensionState?.max ?? param.max ?? 1;
        const rawValue = parameterManager.denormalize(clamped, min, max);
        if (!Number.isFinite(rawValue)) continue;

        params.setDimensionValue(axis, targetDimension, rawValue, SENSOR_SOURCE_ID, priority, undefined);
      } catch (error) {
        console.error('[SensorController] Failed to push phone axis value to voice', voiceId, error);
      }
    }
  }

  /**
   * @param {object|null} voice - Answer for THIS voice instead of the focused one. The phone drives
   * orbiters the screen is not looking at, and asking the focused voice whether an axis is locked or
   * tempo-managed would answer about the wrong orbiter entirely.
   */
  shouldApplySensorToAxis(axis, dimensionId = null, voice = null) {
    if (!axis) {
      return false;
    }

    if (!dimensionId) {
      return true;
    }

    const parameterManager = voice ? voice.parameterManager ?? null : this.parameterManager;
    if (typeof parameterManager?.isParameterDimensionLocked === 'function') {
      try {
        if (parameterManager.isParameterDimensionLocked(axis, dimensionId)) {
          return false;
        }
      } catch (error) {
        console.warn('[SensorController] Failed to check parameter lock state:', error);
      }
    }

    const syncEnabled = syncCoordinator.isEnabled === true;
    if (!syncEnabled) {
      return true;
    }

    const audioEngine = (voice ?? voiceRegistry.getActive())?.audioEngine ?? null;
    if (typeof audioEngine?.hasTempoManagedSpeedTarget === 'function') {
      try {
        if (audioEngine.hasTempoManagedSpeedTarget(axis, dimensionId)) {
          return false;
        }
      } catch (error) {
        console.warn('[SensorController] Failed to check tempo-managed target state:', error);
      }
    }

    return true;
  }

  handleMobileAxisThrottle(axis, normalizedValue, dimensionId = null, { isExternal = false } = {}) {
    const throttleMs = this.getMobilePushThrottleMs();
    if (
      isExternal ||
      !this.isMobileRuntime ||
      throttleMs <= 0
    ) {
      return false;
    }

    const dimensionKey = dimensionId ?? 'default';
    const throttleKey = `${axis}:${dimensionKey}`;
    const now = this.getHighResTimestamp();
    let state = this.axisThrottleState.get(throttleKey);

    if (!state) {
      state = { lastPush: 0, timeoutId: null, pendingValue: undefined, pendingDimensionId: dimensionId };
      this.axisThrottleState.set(throttleKey, state);
    }

    const elapsed = now - state.lastPush;

    if (elapsed >= throttleMs) {
      state.lastPush = now;
      if (state.timeoutId) {
        clearTimeout(state.timeoutId);
        state.timeoutId = null;
      }
      state.pendingValue = undefined;
      state.pendingDimensionId = undefined;
      this.axisThrottleState.set(throttleKey, state);
      return false;
    }

    state.pendingValue = normalizedValue;
    state.pendingDimensionId = dimensionId;

    if (!state.timeoutId) {
      const delay = Math.max(throttleMs - elapsed, 0);
      state.timeoutId = setTimeout(() => {
        const currentState = this.axisThrottleState.get(throttleKey);
        if (!currentState) {
          return;
        }

        currentState.timeoutId = null;
        const pendingValue = currentState.pendingValue;
        const pendingDimensionId = currentState.pendingDimensionId;
        currentState.pendingValue = undefined;
        currentState.pendingDimensionId = undefined;
        currentState.lastPush = this.getHighResTimestamp();
        this.axisThrottleState.set(throttleKey, currentState);

        if (typeof pendingValue !== 'undefined') {
          this.pushAxisValue(axis, pendingValue, pendingDimensionId, { skipThrottle: true, isExternal: false });
        }
      }, delay);
    }

    this.axisThrottleState.set(throttleKey, state);
    return true;
  }

  clearMobileThrottleState() {
    this.axisThrottleState.forEach((entry) => {
      if (entry?.timeoutId) {
        clearTimeout(entry.timeoutId);
      }
    });
    this.axisThrottleState.clear();
  }

  getMobilePushThrottleMs() {
    const dynamicThrottle = Number(SENSOR_FEEDBACK_THROTTLE_MS);
    return Number.isFinite(dynamicThrottle) && dynamicThrottle > 0 ? dynamicThrottle : 0;
  }

  getHighResTimestamp() {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      return performance.now();
    }
    return Date.now();
  }

    /**
     * Clamps a value from one range to another.
     * @param {number} value - The value to clamp.
     * @param {number} min - The minimum value.
     * @param {number} max - The maximum value.
     * @returns {number} The clamped value.
     */
    clamp(value, min, max) {
        return MathUtils.clamp(value, min, max);
    }

        /**
         * Maps a value from one range to another.
         * @param {number} val - The value to map.
         * @param {number} inMin - Input range minimum.
         * @param {number} inMax - Input range maximum.
         * @param {number} outMin - Output range minimum.
         * @param {number} outMax - Output range maximum.
         * @returns {number} The mapped value.
         */
        mapRange(val, inMin, inMax, outMin, outMax) {
            return MathUtils.clamp(
                ((val - inMin) / (inMax - inMin)) * (outMax - outMin) + outMin,
                outMin,
                outMax
            );
        }

    /**
     * Handles `devicemotion` events to calculate translation distance based on Y-axis acceleration.
     * @param {DeviceMotionEvent} event - The motion event containing acceleration data.
     */
    handleDeviceMotion(event) {
        if (!this.calibrated) {
            // Ignore motion events until calibration is complete
            return;
        }

        try {
            const currentTime = event.timeStamp;
            const deltaTime = this.lastTimestamp ? (currentTime - this.lastTimestamp) / 1000 : 0; // Convert ms to seconds
            this.lastTimestamp = currentTime;

            // Get Y-axis acceleration and remove the initial calibration offset
            const accY = event.accelerationIncludingGravity.y || 0;
            const deltaAccY = accY - this.initialAccelerationY;

            // Apply a simple low-pass filter to reduce noise
            const alpha = 0.8; // Smoothing factor
            const filteredAccY = alpha * deltaAccY + (1 - alpha) * (this.previousAccY || 0);
            this.previousAccY = filteredAccY;

            // Integrate acceleration to get velocity
            this.velocityY += filteredAccY * deltaTime;

            // Integrate velocity to get position
            this.positionY += this.velocityY * deltaTime;

            // Normalize distance (0 near, 1 at 0.8 meters)
            const normalizedDistance = Math.min(Math.abs(this.positionY) / 0.8, 1);

            // Update user manager if 'distance' axis is active
            const activeDistanceDimensions = this.getActiveDimensionsForAxis('distance');
            if (activeDistanceDimensions.length) {
                // Assuming 0.5 is center for distance as well
                const distanceNorm = this.mapRange(normalizedDistance, 0, 1, 0, 1);
                activeDistanceDimensions.forEach((dimensionKey) => {
                    this.pushAxisValue('distance', distanceNorm, dimensionKey);
                });
            }

            // Optionally, reset position and velocity if device is stationary (to prevent drift)
            if (Math.abs(filteredAccY) < 0.05) { // Threshold for considering the device as stationary
                this.velocityY *= 0.9; // Dampen velocity
                this.positionY *= 0.9; // Dampen position
            }

            // Optionally, expose the distance for debugging
        } catch (error) {
            console.error('SensorController: Error in handleDeviceMotion:', error);
        }
    }

    /**
     * Maps normalized parameters and updates the `parameterManager`.
     * @private
     */
    updateParameters() {
        try {
            // Since we're directly mapping normalized values in processSensorData,
            // this method can be simplified or removed if not needed.
            // If additional processing is required, implement here.
        } catch (error) {
            console.error('SensorController: Error in updateParameters:', error);
        }
    }

    /**
     * Is this device feeding `axis` on the focused deck's current dimension? The DEVICE flag, not
     * the switch — a deck's switch is answered by `sensorSwitches`, which this device follows.
     * @param {('x'|'y'|'z'|'distance')} axis
     * @returns {boolean}
     */
    isAxisEnabled(axis) {
        return this.activeAxes[axis] === true;
    }

    /**
     * Start or stop feeding `axis` on the dimension the focused deck is playing — the DEVICE half of
     * a switch. Only ever called from behind a switch (`sensorSwitches` drives it when a switch is
     * pressed, and again when the focus or the dimension moves), never by a toggle directly: a toggle
     * presses the deck's switch, and the switch works the device. Enabling lazily calls
     * `activateSensors()`, which may prompt for the motion permission on iOS — the player pressed it,
     * so that prompt is theirs. `distance` has no switch, so this flag is its only state.
     * @param {('x'|'y'|'z'|'distance')} axis
     * @param {boolean} isActive
     */
    setAxisActive(axis, isActive) {
        if (!(axis in this.activeAxes)) {
            return;
        }
        const on = Boolean(isActive);
        this.activeAxes[axis] = on;
        debug('axis', { axis, on, dimension: this.getCurrentDimensionId() });
        if (axis === 'distance') return;
        if (on) {
            if (!this.isSensorActive) this.activateSensors();
        } else if (!this.hasAnySensorAxisActive() && this.isSensorActive) {
            this.stopListening();
        }
    }

    /**
     * Throttles a function call to a specified limit.
     * Prevents excessive executions of expensive operations.
     * @param {Function} func - The function to throttle.
     * @param {number} limit - The time in milliseconds to throttle executions.
     * @returns {Function} The throttled function.
     */
    throttle(func, limit) {
        let inThrottle;
        return function (...args) {
            if (!inThrottle) {
                func.apply(this, args);
                inThrottle = true;
                setTimeout(() => (inThrottle = false), limit);
            }
        };
    }

    /**
     * Normalizes a toggle state to a boolean.
     * @param {*} rawState - The raw state value from the toggle element.
     * @returns {boolean} The normalized boolean state.
     */
    normalizeToggleState(rawState) {
        if (typeof rawState === 'boolean') {
            return rawState;
        }
        if (rawState === null || typeof rawState === 'undefined') {
            return false;
        }
        const numeric = Number(rawState);
        if (!Number.isNaN(numeric)) {
            return numeric === 1;
        }
        if (typeof rawState === 'string') {
            const lowered = rawState.toLowerCase();
            if (lowered === 'true') return true;
            if (lowered === 'false') return false;
        }
        return Boolean(rawState);
    }

    /**
     * Converts an external toggle key back to the controller axis identifier.
     * @param {string} key - The toggle key (e.g., 'sX').
     * @returns {string|null} The axis ('x', 'y', or 'z') or null if unsupported.
     */
    toggleKeyToAxis(key) {
        switch (key) {
            case 'sX':
                return 'x';
            case 'sY':
                return 'y';
            case 'sZ':
                return 'z';
            default:
                return null;
        }
    }

    getScopedContext() {
        // Scope resolution consolidated into one shared resolver (was duplicated
        // verbatim across CosmicLFO / Sensors / MIDI). Sensors uses the base resolve with
        // no ParameterManager fallback, matching the prior behaviour exactly.
        return resolveScopedContext();
    }

    /**
     * Lazily bind the {@link InputSource} this controller pushes through. Created from
     * `parameterManager` (the ParameterManager) the first time it's available; null until then.
     * @returns {import('./source/InputSource.ts').InputSource | null}
     */
    _inputSource() {
        if (!this.parameterManager) return null;
        if (!this._inputSourceHandle) {
            // A GENERATOR: tilt is a machine stream of absolute samples — the next reading
            // supersedes any rollback, and nobody's hand rests on the axis it drives.
            this._inputSourceHandle = createInputRouter(this.parameterManager)
                .source(SENSOR_SOURCE_ID, getPriority('sensor-x'), { stream: 'generator' });
        }
        return this._inputSourceHandle;
    }

    /** Point sensor output at the FOCUSED voice's ParameterManager (falling back to the one the
     *  controller was constructed with) and drop the cached input router so it rebuilds against the new
     *  target. Called on construction and on every active-voice change. The router is a plain PM-holding
     *  wrapper (no listeners), so re-creating it is cheap and leak-free. */
    _rebindToActiveVoice() {
        const active = voiceRegistry.getActive();
        if (!active) return; // no active voice (teardown) → keep the current target
        // Always target the ACTIVE voice — even if its PM is momentarily absent (target null → the
        // `commitAxisValue` guard drives NOTHING until it attaches), never the previously-focused voice.
        // In practice a voice registers with its PM synchronously, so this resolves to a real PM.
        const activePm = active.parameterManager ?? null;
        if (activePm !== this.parameterManager) {
            this.parameterManager = activePm;
            this._inputSourceHandle = null;
        }
    }

    /**
     * A toggle pressed on the paired phone. The same act as pressing the toggle on screen, on EVERY
     * deck the phone drives — each deck's own switch, on the dimension it is playing — and the store
     * announces it back to the phone. Never moves the screen's focus. With no deck selected on the
     * phone it presses nothing.
     * @param {string} key - The external toggle key ('sX', 'sY', 'sZ').
     * @param {boolean|number|string} value - The desired state.
     */
    applyRemoteToggle(key, value) {
        const axis = this.toggleKeyToAxis(key);
        if (!axis) {
            console.warn(`SensorController: Unknown toggle key '${key}' received from remote.`);
            return;
        }
        const on = this.normalizeToggleState(value);
        for (const voiceId of externalTargetIds()) {
            pressSensor(voiceId, axis, on, { source: 'remote' });
        }
    }

    /**
     * One deck's switches on one dimension (its own when none is named), in the phone's
     * `sX/sY/sZ` shape. `voiceId: null` means no deck: every lamp off.
     * @returns {{sX: boolean, sY: boolean, sZ: boolean}}
     */
    getToggleStates(dimensionId = null, voiceId = defaultSensorVoiceId()) {
        if (voiceId === null) return { sX: false, sY: false, sZ: false };
        const dimension = !dimensionId || dimensionId === 'default' ? null : dimensionId;
        return {
            sX: isSensorEnabledOn(voiceId, dimension, 'x'),
            sY: isSensorEnabledOn(voiceId, dimension, 'y'),
            sZ: isSensorEnabledOn(voiceId, dimension, 'z'),
        };
    }

    /**
     * Loads dynamic SVG icons for the calibration button.
     * Ensures the SVG is fetched and injected dynamically.
     * @public
     */
    loadCalibrationButtonSVG() {
        const calibrationButtonIcon = document.querySelector('#sensor-calibration .button-icon');
        if (!calibrationButtonIcon) {
            debug('legacy calibration button icon absent');
            return;
        }

        const src = calibrationButtonIcon.getAttribute('data-src');
        if (src) {
            this.fetchAndSetSVG(src, calibrationButtonIcon, true);
        }
    }

    /**
     * Fetches and sets SVG content into a specified element.
     * @param {string} src - URL of the SVG file to fetch.
     * @param {HTMLElement} element - DOM element to insert the fetched SVG into.
     * @param {boolean} [isInline=true] - Whether to insert the SVG inline.
     * @private
     */
    fetchAndSetSVG(src, element, isInline = true) {
        if (!isInline) return;

        fetch(src)
            .then(response => {
                if (!response.ok) throw new Error(`Failed to load SVG: ${src}`);
                return response.text();
            })
            .then(svgContent => {
                const parser = new DOMParser();
                const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
                const svgElement = svgDoc.documentElement;

                if (svgElement && svgElement.tagName.toLowerCase() === 'svg') {
                    svgElement.setAttribute('fill', 'currentColor');
                    svgElement.setAttribute('role', 'img');
                    svgElement.classList.add('icon-svg');
                    element.innerHTML = ''; // Clear existing content
                    element.appendChild(svgElement); // Insert the SVG
                } else {
                    console.error(`Invalid SVG content fetched from: ${src}`);
                }
            })
            .catch(error => console.error(`SensorController: Error loading SVG from ${src}:`, error));
    }

    /**
     * Initializes the sensor calibration button and SVG loading.
     * Call this method after the DOM is fully loaded.
     * @public
     */
    initializeCalibrationButton() {
        const calibrationButton = document.getElementById('sensor-calibration');
        if (!calibrationButton) {
            debug('legacy calibration button absent');
            return;
        }

        // Attach an event listener for calibration
        calibrationButton.addEventListener('click', () => {
            calibrationButton.disabled = true; // Disable button during calibration
            this.calibrateDevice();

            // Re-enable the button after a short delay to prevent multiple calibrations
            setTimeout(() => {
                calibrationButton.disabled = false;
            }, 6000); // 6 seconds (adjust based on calibration time)
        });

        // Load the SVG dynamically
        this.loadCalibrationButtonSVG();
    }

    /**
     * Sets sensor values externally (e.g., via WebRTC).
     * @param {Object} data - Sensor data from external device.
     * @param {number} data.alpha - Rotation around Z axis (degrees).
     * @param {number} data.beta - Rotation around X axis (degrees).
     * @param {number} data.gamma - Rotation around Y axis (degrees).
     */
    setExternalSensorData(data) {
        if (!this.useExternalSensors) {
          console.warn('[SensorController] External sensors not enabled.');
          return;
        }
      
        // e.g. data = { alpha: 123, beta: 45, gamma: -67 }
        this.lastExternalSensorData = data; 
      
        // The dimension the phone names needs no forwarding: a sample drives exactly the dimensions
        // whose switch is on, and the phone's tab is one of them or it is not.
        this.processSensorData(data, true);
      }
    /**
     * Switch between internal and external sensor sources.
     * @param {boolean} useExternal - If true, use external sensors; otherwise, use internal.
     */
    switchSensorSource(useExternal) {
        this.useExternalSensors = useExternal;
        this.useInternalSensors = !useExternal;

        if (useExternal) {
            this.stopListening(); // Stop internal sensor listeners
        } else {
            this.startListening(); // Start internal sensor listeners
        }
    }
}
